import {
  type QueryFunctionContext,
  type QueryKey,
  replaceEqualDeep,
} from '@tanstack/vue-query';
import type {
  KioskResponse,
  PartnerResponse,
  PrintLogoStateResponse,
  StyleResponse,
} from '@/shared/types/api';
import {
  type ApiQuery,
  api,
  callBreadcrumbs,
  type InstrumentConfig,
  instrument,
  unwrap,
} from '../client';

export interface ConfigQueryOptions {
  // A background refresh fails on every network blip of a venue, and the
  // kiosk keeps its config then: breadcrumbs only, no Sentry issue.
  quiet?: boolean;
}

interface Telemetry {
  span: string;
  errorTag: string;
  trackErrorCode: string;
  tags: Record<string, unknown>;
}

function telemetryConfig<T>(
  { span, errorTag, trackErrorCode, tags }: Telemetry,
  quiet = false
): InstrumentConfig<T> {
  return {
    span,
    errorTag,
    tags,
    ...callBreadcrumbs<T>(`${span} started`, `${span} done`, tags),
    ...(quiet ? { skipSentryCapture: true } : { trackErrorCode }),
  };
}

// TanStack aborts the read through `signal` when a caller cancels the query.
function instrumented<T>(
  queryKey: QueryKey,
  read: (signal: AbortSignal) => Promise<T>,
  telemetry: Telemetry,
  { quiet }: ConfigQueryOptions = {}
): ApiQuery<T> {
  const config = telemetryConfig<T>(telemetry, quiet);
  return {
    queryKey,
    queryFn: ({ signal }: QueryFunctionContext): Promise<T> =>
      instrument<T>(config, () => read(signal)),
  };
}

export function kioskQuery(
  slug: string,
  options?: ConfigQueryOptions
): ApiQuery<KioskResponse> {
  return instrumented(
    ['kiosk', slug],
    (signal) =>
      unwrap(
        api.GET('/v1/kiosks/{kiosk_slug}', {
          params: { path: { kiosk_slug: slug } },
          signal,
        })
      ),
    {
      span: 'api.get_kiosk',
      errorTag: 'kiosks_get_kiosk_by_slug',
      trackErrorCode: 'api_error_kiosk',
      tags: { 'kiosk.slug': slug },
    },
    options
  );
}

export function partnerQuery(
  partnerId: string,
  options?: ConfigQueryOptions
): ApiQuery<PartnerResponse> {
  return instrumented(
    ['partner', partnerId],
    (signal) =>
      unwrap(
        api.GET('/v1/partners/{partner_id}', {
          params: { path: { partner_id: partnerId } },
          signal,
        })
      ),
    {
      span: 'api.get_partner',
      errorTag: 'partners_get_partner_by_id',
      trackErrorCode: 'api_error_partner',
      tags: { 'partner.id': partnerId },
    },
    options
  );
}

// The backend picks preview_url at random between the two previews on every
// read: the kiosk keeps the one it shows, or every refresh sees a new style.
function keepShownPreview(old: unknown, next: unknown): unknown {
  const shown = (old as StyleResponse | undefined)?.preview_url;
  const style = next as StyleResponse;
  const stable =
    shown &&
    (shown === style.preview_url_male || shown === style.preview_url_female)
      ? { ...style, preview_url: shown }
      : style;
  return replaceEqualDeep(old, stable);
}

export function styleQuery(
  styleId: string,
  options?: ConfigQueryOptions
): ApiQuery<StyleResponse> {
  return {
    ...instrumented<StyleResponse>(
      ['style', styleId],
      (signal) =>
        unwrap(
          api.GET('/v1/styles/{style_id}', {
            params: { path: { style_id: styleId } },
            signal,
          })
        ),
      {
        span: 'api.get_style',
        errorTag: 'styles_get_style',
        trackErrorCode: 'api_error_style',
        tags: { 'style.id': styleId },
      },
      options
    ),
    structuralSharing: keepShownPreview,
  };
}

export interface PrintLogoVariables {
  kioskSlug: string;
  enabled: boolean;
}

export function printLogoMutation(): {
  mutationFn: (
    variables: PrintLogoVariables
  ) => Promise<PrintLogoStateResponse>;
} {
  return {
    mutationFn: ({
      kioskSlug,
      enabled,
    }: PrintLogoVariables): Promise<PrintLogoStateResponse> =>
      instrument(
        telemetryConfig<PrintLogoStateResponse>({
          span: 'api.set_print_logo',
          errorTag: 'kiosks_set_print_logo_enabled',
          trackErrorCode: 'api_error_print_logo',
          tags: { 'kiosk.slug': kioskSlug, enabled },
        }),
        () =>
          unwrap(
            api.PUT('/v1/kiosks/{kiosk_slug}/print-logo', {
              params: { path: { kiosk_slug: kioskSlug } },
              body: { enabled },
            })
          )
      ),
  };
}
